import { useCallback, useEffect, useState } from 'react';
import {
  makeStyles,
  tokens,
  Text,
  Button,
  Table,
  TableHeader,
  TableRow,
  TableHeaderCell,
  TableBody,
  TableCell,
  Tooltip,
} from '@fluentui/react-components';
import {
  PersonRegular,
  CallRegular,
  BotRegular,
  ArrowRoutingRegular,
  ArrowDownloadRegular,
} from '@fluentui/react-icons';
import type {
  CallLegsResponse,
  CallLegParticipant,
  VoiceIQAssessment,
  VoiceIQSourceAssessment,
} from '../../types';
import { api } from '../../services/api';
import { useTimezone } from '../common/TimezoneProvider';
import { CallLegReportingTimeDisplay } from '../common/SourceValueDisplay';
import { StatusPill, type StatusPillTone } from '../common/StatusPill';
import { LoadingState } from '../common/LoadingState';
import { ErrorState } from '../common/ErrorState';
import { EmptyState } from '../common/EmptyState';

const useStyles = makeStyles({
  legQualityBadge: {
    marginLeft: tokens.spacingHorizontalXS,
  },
  container: {
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalL}`,
    backgroundColor: tokens.colorNeutralBackground2,
    borderBottom: `1px solid ${tokens.colorNeutralStroke1}`,
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    marginBottom: tokens.spacingVerticalS,
  },
  participant: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
  },
  phoneNumber: {
    fontFamily: 'monospace',
    fontSize: tokens.fontSizeBase200,
  },
  duration: {
    fontFamily: 'monospace',
  },
  sourceCell: {
    minWidth: '50px',
  },
  viewToggle: {
    marginLeft: 'auto',
    display: 'flex',
    gap: tokens.spacingHorizontalXS,
  },
  assessment: {
    padding: tokens.spacingVerticalM,
    marginBottom: tokens.spacingVerticalM,
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground1,
  },
  assessmentHeader: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: tokens.spacingHorizontalS,
    marginBottom: tokens.spacingVerticalS,
  },
  sourceRow: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: tokens.spacingHorizontalS,
    marginTop: tokens.spacingVerticalXS,
  },
  technicalEvidence: {
    marginTop: tokens.spacingVerticalS,
    paddingTop: tokens.spacingVerticalS,
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  streamRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: tokens.spacingHorizontalS,
    marginTop: tokens.spacingVerticalXS,
    fontFamily: 'monospace',
  },
});

type QualityStatus = VoiceIQAssessment['overall_status'];

function qualityColor(status: QualityStatus): 'success' | 'danger' | 'warning' | 'informative' {
  if (status === 'healthy') return 'success';
  if (status === 'poor') return 'danger';
  if (status === 'degraded') return 'warning';
  return 'informative';
}

function qualityTone(status: QualityStatus): StatusPillTone {
  const color = qualityColor(status);
  if (color === 'success') return 'positive';
  if (color === 'danger') return 'negative';
  if (color === 'warning') return 'warning';
  return 'neutral';
}

const reasonLabels: Record<string, string> = {
  all_required_paths_healthy: 'Both the Ribbon and Microsoft paths have sufficient healthy evidence.',
  relevant_path_poor: 'At least one trustworthy part of the connected media path proves an issue.',
  relevant_path_degraded: 'At least one trustworthy part of the connected media path shows degradation.',
  required_path_incomplete: 'One or more required media paths do not have enough trustworthy evidence.',
  ambiguous_or_missing_correlation: 'The Ribbon call cannot be linked to exactly one Teams call with full confidence.',
  tenant_mismatch: 'The linked evidence did not pass tenant-isolation checks.',
  unsupported_sbc_topology: 'This is not the simple two-leg Direct Routing shape supported by this preview.',
  unsupported_graph_topology: 'This call contains more than one Teams session or an unsupported service path.',
  unsupported_participant_shape: 'This preview supports one connected person and one external phone endpoint.',
  no_connected_human_interval: 'A connected human interval could not be proven.',
  unsupported_media: 'A supported audio path could not be proven.',
};

function readableReason(reason: string): string {
  return reasonLabels[reason] || reason.split('_').join(' ');
}

function SourceStatus({ label, source }: { label: string; source: VoiceIQSourceAssessment }) {
  const styles = useStyles();
  return (
    <div className={styles.sourceRow}>
      <Text size={200}>{label}</Text>
      <StatusPill tone={qualityTone(source.status)}>
        {source.status}
      </StatusPill>
      <Text size={200}>{readableReason(source.reason)}</Text>
      <Text size={100}>({source.completeness.split('_').join(' ')})</Text>
    </div>
  );
}

function VoiceIQPreview({ assessment, technical }: { assessment: VoiceIQAssessment; technical: boolean }) {
  const styles = useStyles();
  const { formatTime } = useTimezone();
  const sources = [assessment.ribbon, assessment.microsoft];
  return (
    <div className={styles.assessment}>
      <div className={styles.assessmentHeader}>
        <Text weight="semibold">VoiceIQ preview</Text>
        <StatusPill tone={qualityTone(assessment.overall_status)}>
          {assessment.headline}
        </StatusPill>
        <Text size={200}>{readableReason(assessment.overall_reason)}</Text>
      </div>
      {technical && (
        <div className={styles.technicalEvidence}>
          <Text size={200} weight="semibold">
            Technical evidence · policy {assessment.policy}
          </Text>
          {assessment.correlation_method && (
            <Text size={100} block>
              Correlation: {assessment.correlation_method} ({assessment.correlation_confidence})
              {assessment.freshness ? ` · evidence current to ${formatTime(assessment.freshness)}` : ''}
            </Text>
          )}
          <SourceStatus label="Ribbon path" source={assessment.ribbon} />
          <SourceStatus label="Microsoft path" source={assessment.microsoft} />
          {sources.flatMap((source) => source.streams).map((stream, index) => (
            <div
              className={styles.streamRow}
              key={`${stream.source}-${stream.session_id || stream.source_record_id}-${stream.segment_id || index}-${stream.direction || index}`}
            >
              <Text size={100}>{stream.source}</Text>
              <Text size={100}>{stream.direction || 'direction unknown'}</Text>
              <StatusPill tone={qualityTone(stream.status)} outline>
                {stream.status}
              </StatusPill>
              <Text size={100}>
                {Object.entries(stream.metrics)
                  .filter(([, value]) => value !== null)
                  .map(([key, value]) => `${key}=${value}`)
                  .join(' · ') || 'no usable metrics'}
              </Text>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function formatDuration(seconds: number | null): string {
  if (seconds === null) return '-';
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

function formatPhoneNumber(phone: string | null): string {
  if (!phone) return '';
  const digitsOnly = phone.replace(/\D/g, '');
  const tenDigit = digitsOnly.length === 11 && digitsOnly.startsWith('1')
    ? digitsOnly.slice(1)
    : digitsOnly;
  if (tenDigit.length === 10) {
    return `(${tenDigit.slice(0, 3)}) ${tenDigit.slice(3, 6)}-${tenDigit.slice(6)}`;
  }
  return phone;
}

function ParticipantDisplay({ participant }: { participant: CallLegParticipant }) {
  const styles = useStyles();
  const type = participant.identity_type;

  let icon = <CallRegular />;
  let badge: string | null = null;

  if (type === 'user') {
    icon = <PersonRegular />;
    if (participant.identity_subtype === 'voicemail') {
      badge = 'VM';
    }
  } else if (type === 'auto_attendant' || type === 'applicationInstance') {
    icon = <BotRegular />;
    badge = participant.resource_type === 'call_queue' ? 'CQ' : 'AA';
  } else if (type === 'call_queue') {
    icon = <ArrowRoutingRegular />;
    badge = 'CQ';
  }

  const displayName = participant.display_name;
  const phone = participant.phone_number ? formatPhoneNumber(participant.phone_number) : null;

  return (
    <div className={styles.participant}>
      {icon}
      {badge && (
        <StatusPill tone="info">{badge}</StatusPill>
      )}
      {displayName && <Text size={200}>{displayName}</Text>}
      {phone && <span className={styles.phoneNumber}>{phone}</span>}
      {!displayName && !phone && <Text size={200}>Unknown</Text>}
    </div>
  );
}

function OutcomeBadge({ outcome }: { outcome: string | null }) {
  if (!outcome) return <Text size={200}>-</Text>;

  const toneMap: Record<string, StatusPillTone> = {
    answered: 'positive',
    failed: 'negative',
    no_answer: 'warning',
    transferred: 'info',
    voicemail: 'warning',
  };

  return (
    <StatusPill tone={toneMap[outcome] || 'neutral'}>
      {outcome.replace('_', ' ')}
    </StatusPill>
  );
}

interface CallLegsDetailProps {
  data: CallLegsResponse | null;
  isLoading: boolean;
  error: string | null;
  multiSessionId?: string;
  /** Resolved `can_see_technical_details` -- never tenant membership. */
  canSeeTechnical?: boolean;
  /** Raw debug export is platform-admin only (`actual_role`), matching the API. */
  canExportDebug?: boolean;
}

export function CallLegsDetail({
  data,
  isLoading,
  error,
  multiSessionId,
  canSeeTechnical,
  canExportDebug,
}: CallLegsDetailProps) {
  const styles = useStyles();
  const { formatTime } = useTimezone();
  const [exporting, setExporting] = useState(false);
  const [viewMode, setViewMode] = useState<'user' | 'technical'>('user');

  useEffect(() => {
    setViewMode('user');
  }, [multiSessionId]);

  const handleExport = useCallback(async () => {
    if (!multiSessionId) return;
    setExporting(true);
    try {
      const debugData = await api.getCallLegsDebug(multiSessionId);
      const blob = new Blob([JSON.stringify(debugData, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `call-debug-${multiSessionId}.json`;
      a.style.display = 'none';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error('Export failed:', e);
    } finally {
      setExporting(false);
    }
  }, [multiSessionId]);

  if (isLoading) {
    return (
      <div className={styles.container}>
        <LoadingState variant="panel" height={60} />
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.container}>
        <ErrorState message={error} />
      </div>
    );
  }

  if (!data || data.legs.length === 0) {
    return (
      <div className={styles.container}>
        <EmptyState compact title="No leg details available" />
      </div>
    );
  }

  const userLegs = data.user_legs?.length ? data.user_legs : data.legs;
  const technicalLegs = data.technical_legs?.length ? data.technical_legs : data.legs;
  const activeLegs = viewMode === 'technical' ? technicalLegs : userLegs;
  const canShowTechnical = Boolean(canSeeTechnical && technicalLegs.length > 0);
  const titleLabel = viewMode === 'technical' ? 'Technical Journey' : 'Call Journey';

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <Text size={200} weight="semibold">
          {titleLabel} ({activeLegs.length} legs)
        </Text>
        {data.has_teams_sessions && (
          <Tooltip content="Includes Teams internal routing data" relationship="label">
            <StatusPill tone="info">Teams</StatusPill>
          </Tooltip>
        )}
        {canShowTechnical && (
          <div className={styles.viewToggle}>
            <Button
              size="small"
              appearance={viewMode === 'user' ? 'primary' : 'subtle'}
              onClick={() => setViewMode('user')}
            >
              Summary
            </Button>
            <Button
              size="small"
              appearance={viewMode === 'technical' ? 'primary' : 'subtle'}
              onClick={() => setViewMode('technical')}
            >
              Technical
            </Button>
          </div>
        )}
        {canExportDebug && multiSessionId && (
          <Button
            size="small"
            appearance="subtle"
            icon={<ArrowDownloadRegular />}
            onClick={handleExport}
            disabled={exporting}
          >
            {exporting ? 'Exporting...' : 'Export Debug'}
          </Button>
        )}
      </div>
      {data.voiceiq_assessment && (
        <VoiceIQPreview
          assessment={data.voiceiq_assessment}
          technical={viewMode === 'technical'}
        />
      )}
      <Table size="small">
        <TableHeader>
          <TableRow>
            <TableHeaderCell style={{ width: '30px' }}>#</TableHeaderCell>
            <TableHeaderCell className={styles.sourceCell}>Source</TableHeaderCell>
            <TableHeaderCell>From</TableHeaderCell>
            <TableHeaderCell>To</TableHeaderCell>
            <TableHeaderCell>Time</TableHeaderCell>
            <TableHeaderCell>Duration</TableHeaderCell>
            <TableHeaderCell>Outcome</TableHeaderCell>
          </TableRow>
        </TableHeader>
        <TableBody>
          {activeLegs.map((leg, index) => (
            <TableRow key={`${leg.source}-${leg.session_id || index}`}>
              <TableCell>
                <Text size={200}>{index + 1}</Text>
              </TableCell>
              <TableCell className={styles.sourceCell}>
                <StatusPill tone="info" outline>
                  {leg.source === 'teams' ? 'Teams' : 'SBC'}
                </StatusPill>
              </TableCell>
              <TableCell>
                <ParticipantDisplay participant={leg.caller} />
              </TableCell>
              <TableCell>
                <ParticipantDisplay participant={leg.callee} />
              </TableCell>
              <TableCell>
                <CallLegReportingTimeDisplay leg={leg} format={formatTime} />
                {leg.incomplete_leg_evidence && <Text size={200}>Timing incomplete</Text>}
              </TableCell>
              <TableCell className={styles.duration}>
                <Text size={200}>{formatDuration(leg.duration_seconds)}</Text>
              </TableCell>
              <TableCell>
                <OutcomeBadge outcome={leg.outcome} />
                {data.voiceiq_assessment?.supported_shape
                  && data.voiceiq_assessment.connected_session_id === leg.session_id && (
                    <StatusPill
                      tone={qualityTone(data.voiceiq_assessment.overall_status)}
                      className={styles.legQualityBadge}
                    >
                      {data.voiceiq_assessment.headline}
                    </StatusPill>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
